import { useCallback, useEffect, useRef, useState } from 'react';
import agencyOverviewService from '../../../../services/agencyOverviewService';
import socketService from '../../../../services/socketService';

const FALLBACK_REFRESH_MS = 60_000;

export function useAgencyOverview(range) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);
  const requestIdRef = useRef(0);
  const queuedInvalidationRef = useRef(false);
  const mountedRef = useRef(false);
  const loadRef = useRef(null);

  const load = useCallback(async ({ background = false, ifBusy = 'replace' } = {}) => {
    if (controllerRef.current && ifBusy !== 'replace') {
      if (ifBusy === 'queue') queuedInvalidationRef.current = true;
      return null;
    }
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const requestId = ++requestIdRef.current;

    if (background) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const next = await agencyOverviewService.getOverview(range, { signal: controller.signal });
      if (requestId === requestIdRef.current) setData(next);
      return next;
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError' || err.code === 'ERR_CANCELED') return null;
      if (requestId === requestIdRef.current) setError(err);
      throw err;
    } finally {
      if (requestId === requestIdRef.current) {
        controllerRef.current = null;
        setLoading(false);
        setRefreshing(false);
        if (queuedInvalidationRef.current && mountedRef.current) {
          queuedInvalidationRef.current = false;
          Promise.resolve().then(() => {
            if (mountedRef.current) {
              loadRef.current?.({ background: true, ifBusy: 'skip' }).catch(() => {});
            }
          });
        }
      }
    }
  }, [range]);
  loadRef.current = load;

  useEffect(() => {
    mountedRef.current = true;
    load().catch(() => {});
    return () => {
      mountedRef.current = false;
      queuedInvalidationRef.current = false;
      requestIdRef.current += 1;
      controllerRef.current?.abort();
    };
  }, [load]);

  useEffect(() => {
    let timer;
    const invalidate = () => {
      clearTimeout(timer);
      timer = setTimeout(() => load({ background: true, ifBusy: 'queue' }).catch(() => {}), 250);
    };
    const unsubscribe = socketService.on('agency:changed', invalidate);
    return () => {
      clearTimeout(timer);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [load]);

  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') {
        load({ background: true, ifBusy: 'skip' }).catch(() => {});
      }
    };
    const interval = setInterval(refreshIfVisible, FALLBACK_REFRESH_MS);
    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [load]);

  return {
    data,
    loading,
    refreshing,
    error,
    refresh: () => load({ background: true }),
  };
}

export { FALLBACK_REFRESH_MS };
